import { useCallback, useMemo, useState } from 'react';
import { useTheme } from '@emotion/react';
import { FolderOpen } from '@phosphor-icons/react';
import { ComponentDocument } from 'common/stack/ComponentDocuments';
import { parsePackageUrl } from 'common/stack/stackUtils';
import { StackComponentDialog } from './StackComponentDialog';
import { StackContentList } from './StackContentList';

export interface StackFilesProps {
  components: { name: string; identifier: string; package: string }[];
  stack: { identifier: string; details: ComponentDocument }[];
  onIdentifier: (value: { identifier: string; value: string }) => void;
  onVersion: (value: { identifier: string; value: string }) => void;
  onRemove: (identifier: string) => void;
}

export const StackFiles = ({ components, stack, onIdentifier, onVersion, onRemove }: StackFilesProps) => {
  const theme = useTheme();
  const [identifier, setIdentifier] = useState<string | null>(null);

  const component = useMemo(() => {
    if (!identifier) return null;

    const component = components.find((component) => component.identifier === identifier);
    if (!component) return null;

    const { ref = 'main' } = parsePackageUrl(component.package);

    const details = stack.find((item) => item.identifier === identifier);
    if (!details) return null;

    return {
      identifier: component.identifier,
      version: ref,
      type: 'component' as const,
      details: details.details,
    };
  }, [identifier, components, stack]);

  const handleClick = ({ identifier }: { identifier: string }) => setIdentifier(identifier);

  const handleIdentifierChange = useCallback(
    (value: string) => component && onIdentifier({ identifier: component.identifier, value }),
    [component, onIdentifier]
  );

  const handleVersionChange = useCallback(
    (value: string) => component && onVersion({ identifier: component.identifier, value }),
    [component, onVersion]
  );

  const items = useMemo(
    () =>
      components.map((component) => ({
        label: `/${component.identifier}`,
        component,
      })),
    [components]
  );

  return (
    <>
      <StackContentList
        title={
          <>
            <FolderOpen weight="bold" /> Monorepo
          </>
        }
        items={items}
        prefix={
          <li aria-disabled style={{ color: theme.color.grey[80] }}>
            /.github
          </li>
        }
        postfix={
          <li aria-disabled style={{ color: theme.color.grey[80] }}>
            /diploi.yaml
          </li>
        }
        help="Editing the component identifier will change the folder it is stored in."
        onClick={handleClick}
      />
      <StackComponentDialog
        component={component}
        onIdentifier={handleIdentifierChange}
        onVersion={handleVersionChange}
        onRemove={onRemove}
        onClose={() => setIdentifier(null)}
      />
    </>
  );
};
